import { ComponentChild, ComponentProps, RefObject } from 'preact';
import { useCallback, useRef, useEffect } from 'preact/hooks';
import type { BundleType } from '../resources/nls/bundle';
import { useTranslationBundle } from '../hooks/UNSAFE_useTranslationBundle';
import { getClientHints } from '../utils/PRIVATE_clientHints';
import { mergeInterpolations } from '../utils/UNSAFE_mergeInterpolations';
import { flexitemInterpolations } from '../utils/UNSAFE_interpolations/flexitem';
import type { FlexitemProps } from '../utils/UNSAFE_interpolations/flexitem';
import { classNames } from '../utils/UNSAFE_classNames';
import {
  NavigationListItem,
  NavigationList,
  RemovableNavigationListItem
} from '../UNSAFE_NavigationList';
import { OverflowH } from '../UNSAFE_RedwoodIcons/OverflowH';
import { useTabBarContext } from './useTabBarContext';
import { Dropdown } from '../UNSAFE_Dropdown';
import { Sheet } from '../UNSAFE_Sheet';
import { BaseTabBarItem } from './BaseTabBarItem';
import { useComponentTheme } from '../hooks/UNSAFE_useComponentTheme';
import { TabBarItemVariantOptions } from './themes/TabBarItemStyles.css';
import { TabBarItemRedwoodTheme } from './themes/redwood/TabBarItemTheme';

type FocusableHandle = {
  focus: () => void;
};

export type OverflowSelectionDetail<K> = {
  value: K;
};

export type CloseDetail = {
  /**
   * dismissed represents dismissing the DropDown
   */
  reason: 'dismissed' | 'itemAction' | 'outsideMousedown';
};

type Item<K extends string | number> = Omit<
  ComponentProps<typeof NavigationListItem<K>>,
  'testId'
> & {
  isRemovable?: boolean;
};

export type OverflowTabBarItemProps<K extends string | number> = {
  /**
   * Key of the TabBarItem
   */
  overflowItemKey: K;
  /**
   * Whether to open the popup
   */
  isOpen: boolean;
  /**
   * Property that triggers a callback when DropDown is supposed to be closed.
   */
  onClose?: (detail: CloseDetail) => void;
  /**
   * The icon of the OverflowTabBarItem
   */
  icon?: ComponentChild;
  /**
   * The content to be rendered inside the Badge component.
   */
  badge?: string;
  /**
   * An array of data used by NavigationList to render individual overflow items.
   */
  overflowItems?: Item<K>[];
};

/**
 * An OverflowTabBarItem renders TabBarItems in a device appropriate popup. OverflowTabBarItem is used internally in an OverflowTabBar.
 */

export function OverflowTabBarItem<K extends string | number>({
  overflowItemKey,
  icon,
  badge,
  overflowItems,
  isOpen,
  onClose
}: OverflowTabBarItemProps<K>) {
  const { selection, showFocusRing, isEdgeBottom, onSelect, onRemove, layout } = useTabBarContext();

  const isPointerSelection = useRef<boolean>(false);

  const overflowTabBarItemRef = useRef<HTMLDivElement>(null);
  const navigationListRef = useRef() as RefObject<FocusableHandle>;

  const handleSelectionChange = useCallback<
    Required<ComponentProps<typeof NavigationList>>['onSelectionChange']
  >(
    (detail: { value: string | number; reason?: 'pointer' | 'keyboard' }) => {
      if (detail.reason === 'pointer') {
        isPointerSelection.current = true;
      }
      onSelect?.({ value: detail.value });
      onClose?.({ reason: 'itemAction' });
    },
    [onSelect, onClose]
  );

  const handleDropdownClose = useCallback(
    (e: CloseDetail) => {
      onClose?.({ reason: e.reason });
    },
    [onClose]
  );

  const handleSheetClose = useCallback(() => {
    onClose?.({ reason: 'dismissed' });
  }, [onClose]);

  //When NavigationList steals focus the app resets it. Not always would it be reset to
  //parent of OverflowTabBarItem. Though currentKey would be correct, but focusRing would would not be visible
  //Hence we check for isPointerSelection.current

  const focusRingRequirement = showFocusRing && !isPointerSelection.current && !getIsMobile();

  //The focus should be on NavigationList only when it is open using keyboard
  useEffect(() => {
    if (isOpen && navigationListRef.current && focusRingRequirement) {
      navigationListRef.current.focus();
    }
    isPointerSelection.current = false;
  }, [isOpen, focusRingRequirement]);

  const overflowContainsKey = (searchKey: K) => {
    return overflowItems?.find((item) => item.itemKey === searchKey);
  };

  const translations = useTranslationBundle<BundleType>('@oracle/oraclejet-preact');

  const isSelected = selection !== undefined && overflowContainsKey(selection as K);

  const { classes } = useComponentTheme<TabBarItemVariantOptions>(TabBarItemRedwoodTheme, {
    selection: isSelected ? 'isSelected' : 'notSelected',
    edgeBottom: isEdgeBottom ? 'isBottom' : 'notBottom'
  });

  const overflowTabBarItem = classNames([classes]);

  //instead of specifying flex: '1 0 auto' in cssProps var
  const styleInterpolations = mergeInterpolations<FlexitemProps>([
    ...Object.values(flexitemInterpolations)
  ]);
  const flexDimensions = styleInterpolations({
    flex: layout === 'stretch' ? '1 0 auto' : '0 0 auto'
  });

  const getNavigationList = useCallback(() => {
    return (
      <NavigationList
        ref={navigationListRef}
        selection={selection}
        onSelectionChange={handleSelectionChange}
        onRemove={onRemove}>
        {overflowItems?.map((item) =>
          item.isRemovable ? (
            <RemovableNavigationListItem
              itemKey={item.itemKey}
              label={item.label}
              badge={item.badge}
              metadata={item.metadata}
              severity={item.severity}
            />
          ) : (
            <NavigationListItem
              itemKey={item.itemKey}
              label={item.label}
              badge={item.badge}
              metadata={item.metadata}
              severity={item.severity}
            />
          )
        )}
      </NavigationList>
    );
  }, [overflowItems, selection, handleSelectionChange, onRemove]);

  return (
    <>
      {overflowItems && overflowItems.length > 0 && (
        <div ref={overflowTabBarItemRef} class={overflowTabBarItem} style={flexDimensions}>
          <BaseTabBarItem
            itemKey={overflowItemKey}
            label=" "
            icon={icon ? icon : <OverflowH accessibleLabel={translations.overflowItemLabel()} />}
            badge={badge}
            showFocusRingOverride={focusRingRequirement}
          />
        </div>
      )}
      {getIsMobile() ? (
        <Sheet isOpen={isOpen} onClose={handleSheetClose}>
          {getNavigationList()}
        </Sheet>
      ) : (
        <Dropdown
          isOpen={isOpen}
          onClose={handleDropdownClose}
          placement={isEdgeBottom ? 'top-end' : 'bottom-end'}
          anchorRef={overflowTabBarItemRef}>
          {getNavigationList()}
        </Dropdown>
      )}
    </>
  );
}

const getIsMobile = () => {
  const { deviceType } = getClientHints();
  return deviceType === 'phone';
};
